import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, Save, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CategoryPill } from "../lib/categoryIcons";
import HelpButton from "../components/HelpButton";

const CATEGORIES = [
  "Food",
  "Groceries",
  "Transport",
  "Shopping",
  "Entertainment",
  "Bills",
  "Subscriptions",
  "Gifts",
  "Income",
  "Other",
];

export default function MerchantRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  // Local edits { payee_key: category } — flushed to the server on Save.
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/payee-rules");
      setRules(data || []);
    } catch (e) {
      toast.error("Could not load rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (r) => {
    const next = edits[r.payee_key];
    if (!next || next === r.category) return;
    try {
      await api.put(`/payee-rules/${encodeURIComponent(r.payee_key)}`, { category: next });
      toast.success(`${r.payee_sample || r.payee_key} → ${next}`);
      setEdits((e) => {
        const copy = { ...e };
        delete copy[r.payee_key];
        return copy;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Forget the rule "${r.payee_sample || r.payee_key} → ${r.category}"?`)) return;
    try {
      await api.delete(`/payee-rules/${encodeURIComponent(r.payee_key)}`);
      toast.success("Rule removed");
      setRules((rs) => rs.filter((x) => x.payee_key !== r.payee_key));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? rules.filter((r) =>
        (r.payee_sample || r.payee_key).toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q)
      )
    : rules;

  return (
    <div className="space-y-6" data-testid="merchant-rules-page">
      <div className="flex items-center justify-between">
        <Link to="/profile" className="nb-btn nb-btn-secondary !py-2 !px-3 !text-xs">
          <ArrowLeft size={14} strokeWidth={3} /> Back
        </Link>
        <HelpButton topic="merchant-rules" />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} strokeWidth={3} />
          <h1 className="font-display font-black text-3xl">Merchant Rules</h1>
        </div>
        <p className="text-sm font-medium text-[#4a4a4a]">
          Every time you set a category on a transaction, we remember it so future
          txns from the same merchant auto-fill. Edit or delete any rule below.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          size={16}
          strokeWidth={3}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4a4a] pointer-events-none"
        />
        <input
          data-testid="rules-search"
          type="text"
          placeholder="Search merchants or categories…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="nb-input !pl-9"
        />
      </div>

      {loading ? (
        <div className="text-sm font-medium text-[#4a4a4a]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="nb-card p-8 text-center">
          <Sparkles size={24} strokeWidth={3} className="mx-auto mb-2 opacity-40" />
          <div className="font-display font-black text-lg mb-1">
            {rules.length === 0 ? "No rules yet" : "No matches"}
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            {rules.length === 0
              ? "Categorize a few transactions and we'll start learning."
              : "Try a different search."}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="rules-list">
          {filtered.map((r) => {
            const editing = edits[r.payee_key];
            const dirty = editing && editing !== r.category;
            return (
              <div
                key={r.payee_key}
                className="nb-card p-3 flex items-center gap-3"
                data-testid={`rule-row-${r.payee_key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-base truncate">
                    {r.payee_sample || r.payee_key}
                  </div>
                  <div className="text-[10px] font-bold text-[#4a4a4a] flex items-center gap-2">
                    <span className="font-mono">{r.payee_key}</span>
                    <span>·</span>
                    <span>{r.count} txn{r.count === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!dirty && <CategoryPill category={r.category} />}
                  <select
                    data-testid={`rule-cat-${r.payee_key}`}
                    value={editing ?? r.category}
                    onChange={(e) =>
                      setEdits((es) => ({ ...es, [r.payee_key]: e.target.value }))
                    }
                    className="nb-select !py-1 !text-xs !w-28"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  {dirty && (
                    <button
                      data-testid={`rule-save-${r.payee_key}`}
                      onClick={() => save(r)}
                      className="nb-btn nb-btn-dark !py-1 !px-2"
                      title="Save"
                    >
                      <Save size={12} strokeWidth={3} />
                    </button>
                  )}
                  <button
                    data-testid={`rule-del-${r.payee_key}`}
                    onClick={() => del(r)}
                    className="nb-btn nb-btn-secondary !py-1 !px-2"
                    style={{ background: "#f4c2c2" }}
                    title="Delete rule"
                  >
                    <Trash2 size={12} strokeWidth={3} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
